import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Folder, Plus, ChevronRight, ChevronDown, Trash2, Edit2,
  MoreVertical, Loader2, Inbox, X
} from 'lucide-react';
import api from '../../services/api';

const SavedEmails = () => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#10B981');
  const [creating, setCreating] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [editingFolder, setEditingFolder] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const colors = [
    '#10B981', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#14B8A6', '#3B82F6', '#84CC16', '#F97316'
  ];

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      fetchFolderEmails(selectedFolder.id);
    } else {
      setEmails([]);
    }
  }, [selectedFolder]);

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/email-folders/folders');
      setFolders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderEmails = async (folderId) => {
    setEmailsLoading(true);
    try {
      const response = await api.get(`/api/email-folders/folders/${folderId}/emails`);
      setEmails(response.data.emails || []);
    } catch (error) {
      console.error('Failed to fetch folder emails:', error);
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    setCreating(true);
    try {
      await api.post('/api/email-folders/folders', {
        name: newFolderName.trim(),
        color: newFolderColor,
        parent_id: editingFolder?.parent_id || null
      });
      fetchFolders();
      setShowNewFolderModal(false);
      setNewFolderName('');
      setNewFolderColor('#10B981');
    } catch (error) {
      console.error('Failed to create folder:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and unsave all emails in it?`)) return;

    try {
      await api.delete(`/api/email-folders/folders/${folder.id}`);
      if (selectedFolder?.id === folder.id) {
        setSelectedFolder(null);
      }
      fetchFolders();
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  const handleRenameFolder = async (folder, newName) => {
    if (!newName.trim() || newName === folder.name) {
      setEditingFolder(null);
      return;
    }

    try {
      await api.put(`/api/email-folders/folders/${folder.id}`, {
        name: newName.trim()
      });
      fetchFolders();
    } catch (error) {
      console.error('Failed to rename folder:', error);
    } finally {
      setEditingFolder(null);
    }
  };

  const handleUnsaveEmail = async (email) => {
    try {
      await api.post(`/api/email-folders/emails/${email.id}/unsave`);
      fetchFolderEmails(selectedFolder.id);
    } catch (error) {
      console.error('Failed to unsave email:', error);
    }
  };

  const toggleFolderExpand = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  const handleContextMenu = (e, folder) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      folder
    });
  };

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  useEffect(() => {
    if (contextMenu) {
      document.addEventListener('click', closeContextMenu);
      return () => document.removeEventListener('click', closeContextMenu);
    }
  }, [contextMenu, closeContextMenu]);

  const renderFolder = (folder, depth = 0) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders[folder.id];
    const isSelected = selectedFolder?.id === folder.id;

    return (
      <div key={folder.id}>
        <div
          className={`flex items-center px-3 py-2 cursor-pointer rounded-lg mx-2 mb-1 ${
            isSelected
              ? 'bg-virgo-teal-900/50 text-white'
              : 'hover:bg-virgo-navy-600 text-virgo-teal-200'
          }`}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
          onClick={() => setSelectedFolder(folder)}
          onContextMenu={(e) => handleContextMenu(e, folder)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolderExpand(folder.id);
              }}
              className="p-0.5 hover:bg-virgo-navy-500 rounded mr-1"
            >
              {isExpanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}

          <div
            className="w-3 h-3 rounded mr-2 flex-shrink-0"
            style={{ backgroundColor: folder.color || '#10B981' }}
          />

          {editingFolder?.id === folder.id ? (
            <input
              type="text"
              defaultValue={folder.name}
              autoFocus
              onBlur={(e) => handleRenameFolder(folder, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameFolder(folder, e.target.value);
                if (e.key === 'Escape') setEditingFolder(null);
              }}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 px-1 py-0 text-sm bg-virgo-navy-600 border border-virgo-teal-500 rounded text-white"
            />
          ) : (
            <span className="flex-1 text-sm truncate">{folder.name}</span>
          )}

          <span className="text-xs text-virgo-teal-500 ml-2">
            {folder.email_count || 0}
          </span>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {folder.children.map((child) => renderFolder(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (days === 0) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days} days ago`;
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={24} className="animate-spin text-virgo-teal-500" />
        <span className="ml-2 text-virgo-teal-300">Loading saved emails...</span>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)]">
      {/* Folder sidebar */}
      <div className="w-64 bg-virgo-navy-700 rounded-lg mr-4 flex flex-col">
        <div className="p-3 border-b border-virgo-navy-600 flex items-center justify-between">
          <h3 className="text-sm font-medium text-white">Folders</h3>
          <button
            onClick={() => setShowNewFolderModal(true)}
            className="p-1 hover:bg-virgo-navy-600 rounded transition-colors"
            title="Create folder"
          >
            <Plus size={18} className="text-virgo-teal-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {folders.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Folder size={32} className="mx-auto text-virgo-teal-600 mb-2" />
              <p className="text-sm text-virgo-teal-500">No folders yet</p>
              <p className="text-xs text-virgo-teal-600 mt-1">
                Create a folder to organize your saved emails
              </p>
            </div>
          ) : (
            folders.map((folder) => renderFolder(folder))
          )}
        </div>
      </div>

      {/* Email list */}
      <div className="flex-1 bg-virgo-navy-700 rounded-lg overflow-hidden">
        {selectedFolder ? (
          <>
            <div className="p-3 border-b border-virgo-navy-600 flex items-center">
              <div
                className="w-3 h-3 rounded mr-2"
                style={{ backgroundColor: selectedFolder.color || '#10B981' }}
              />
              <h3 className="font-medium text-white">{selectedFolder.name}</h3>
              <span className="ml-2 text-xs text-virgo-teal-500">
                {emails.length} email{emails.length !== 1 ? 's' : ''}
              </span>
            </div>

            {emailsLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 size={24} className="animate-spin text-virgo-teal-500" />
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-12">
                <Mail size={48} className="mx-auto text-virgo-teal-600 mb-3" />
                <p className="text-virgo-teal-400">No emails in this folder</p>
                <p className="text-xs text-virgo-teal-600 mt-1">
                  Swipe left on emails in Mail to save them here
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto h-full">
                {emails.map((email) => (
                  <div
                    key={email.id}
                    className="px-4 py-3 border-b border-virgo-navy-600 hover:bg-virgo-navy-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${
                            email.is_read ? 'text-virgo-teal-200' : 'text-white'
                          }`}>
                            {email.from}
                          </span>
                          <span className="text-xs text-virgo-teal-500">
                            {formatDate(email.received_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate mt-1 ${
                          email.is_read ? 'text-virgo-teal-300' : 'text-virgo-teal-100'
                        }`}>
                          {email.subject || '(no subject)'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleUnsaveEmail(email)}
                        className="ml-3 p-1.5 hover:bg-virgo-navy-500 rounded transition-colors"
                        title="Remove from folder"
                      >
                        <X size={16} className="text-virgo-teal-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full p-8">
            <Inbox size={64} className="text-virgo-teal-700 mb-4" />
            <h3 className="text-lg font-medium text-virgo-teal-300 mb-2">
              Select a folder
            </h3>
            <p className="text-sm text-virgo-teal-500 text-center max-w-xs">
              Choose a folder from the sidebar to view saved emails, or create a new folder to get started.
            </p>
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed bg-virgo-navy-700 border border-virgo-navy-500 rounded-lg shadow-xl py-1 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              setEditingFolder(contextMenu.folder);
              closeContextMenu();
            }}
            className="w-full px-4 py-2 text-sm text-left text-virgo-teal-200 hover:bg-virgo-navy-600 flex items-center"
          >
            <Edit2 size={14} className="mr-2" />
            Rename
          </button>
          <button
            onClick={() => {
              handleDeleteFolder(contextMenu.folder);
              closeContextMenu();
            }}
            className="w-full px-4 py-2 text-sm text-left text-red-400 hover:bg-virgo-navy-600 flex items-center"
          >
            <Trash2 size={14} className="mr-2" />
            Delete
          </button>
        </div>
      )}

      {/* New folder modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-800 rounded-lg shadow-xl w-full max-w-sm mx-4 p-4">
            <h3 className="text-lg font-medium text-white mb-4">Create Folder</h3>

            <div className="mb-4">
              <label className="block text-sm text-virgo-teal-400 mb-1">
                Folder name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                autoFocus
                placeholder="e.g., Receipts, Contracts, Personal"
                className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-500 rounded-lg text-white placeholder-virgo-teal-600 focus:outline-none focus:border-virgo-teal-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-virgo-teal-400 mb-2">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewFolderColor(color)}
                    className={`w-6 h-6 rounded-full transition-transform ${
                      newFolderColor === color ? 'scale-125 ring-2 ring-white' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowNewFolderModal(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-virgo-teal-400 hover:bg-virgo-navy-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={creating || !newFolderName.trim()}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedEmails;
